import { HttpError, isHttpError } from './httpError'
import type { HttpInstance, HttpRequestConfig, HttpResponse, ResponseType } from './types'

const DEFAULT_VALIDATE_STATUS = (status: number) => status >= 200 && status < 300

function base64(input: string): string {
  if (typeof Buffer !== 'undefined') return Buffer.from(input).toString('base64')
  // eslint-disable-next-line no-undef
  return btoa(input)
}

function mergeConfig(defaults: HttpRequestConfig, config: HttpRequestConfig): HttpRequestConfig {
  return {
    ...defaults,
    ...config,
    headers: { ...(defaults.headers || {}), ...(config.headers || {}) },
    retry: config.retry ?? defaults.retry
  }
}

function buildUrl(config: HttpRequestConfig): string {
  const base = (config.baseURL || '').replace(/\/$/, '')
  const path = config.url || ''
  let url = base && !/^https?:\/\//i.test(path) ? `${base}${path.startsWith('/') ? '' : '/'}${path}` : path

  if (config.params) {
    const search =
      config.params instanceof URLSearchParams
        ? config.params
        : new URLSearchParams(
            Object.entries(config.params)
              .filter(([, v]) => v !== undefined && v !== null)
              .map(([k, v]) => [k, String(v)])
          )
    const qs = search.toString()
    if (qs) url += (url.includes('?') ? '&' : '?') + qs
  }
  return url
}

/**
 * Native fetch (undici) rejects URLs that embed credentials (the `user:pass@host`
 * userinfo form), but axios accepted them and converted them to a Basic auth header.
 * Preserve that behavior: strip any userinfo from the URL and return it as an
 * Authorization value.
 */
function stripUrlCredentials(url: string): { url: string; authHeader?: string } {
  if (!/^[a-z][a-z0-9+.-]*:\/\/[^/]*@/i.test(url)) return { url }
  try {
    const parsed = new URL(url)
    if (!parsed.username && !parsed.password) return { url }
    const authHeader = `Basic ${base64(`${decodeURIComponent(parsed.username)}:${decodeURIComponent(parsed.password)}`)}`
    parsed.username = ''
    parsed.password = ''
    return { url: parsed.toString(), authHeader }
  } catch {
    return { url }
  }
}

function isPassthroughBody(data: any): boolean {
  if (typeof data === 'string') return true
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) return true
  if (typeof FormData !== 'undefined' && data instanceof FormData) return true
  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) return true
  if (typeof Blob !== 'undefined' && data instanceof Blob) return true
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) return true
  return false
}

function buildBody(config: HttpRequestConfig, headers: Record<string, string>): BodyInit | undefined {
  const { data } = config
  if (data === undefined || data === null) return undefined
  if (isPassthroughBody(data)) return data as BodyInit
  // Plain object / array → JSON
  const hasContentType = Object.keys(headers).some((h) => h.toLowerCase() === 'content-type')
  if (!hasContentType) headers['Content-Type'] = 'application/json'
  return JSON.stringify(data)
}

async function parseBody(res: Response, responseType: ResponseType): Promise<any> {
  switch (responseType) {
    case 'arraybuffer': {
      const buffer = await res.arrayBuffer()
      // Match axios: the Node http adapter resolves `arraybuffer` to a Buffer,
      // and callers pass `response.data` straight to the AWS SDK / mailer /
      // image libs that expect one. Return a Buffer in Node, ArrayBuffer in the browser.
      return typeof Buffer !== 'undefined' ? Buffer.from(buffer) : buffer
    }
    case 'blob':
      return res.blob()
    case 'text':
      return res.text()
    case 'json':
    default: {
      // Mirror axios: parse JSON, but tolerate empty/non-JSON bodies by
      // falling back to the raw text rather than throwing.
      const text = await res.text()
      if (!text) return undefined
      try {
        return JSON.parse(text)
      } catch {
        return text
      }
    }
  }
}

function headersToObject(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {}
  headers.forEach((value, key) => {
    out[key] = value
  })
  return out
}

function defaultRetryCondition(error: HttpError): boolean {
  // Retry network errors (no response) and 5xx server errors.
  if (!error.response) return true
  return error.response.status >= 500
}

function defaultRetryDelay(retryCount: number): number {
  // Exponential backoff: 100ms, 200ms, 400ms, ... (matches axios-retry's spirit)
  return 2 ** (retryCount - 1) * 100
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Network-level failure (DNS, connection refused, abort/timeout, a body stream that died).
 * undici wraps these as a generic `TypeError: fetch failed` with the real error (ECONNREFUSED,
 * ENOTFOUND, TLS, ...) on `err.cause` — unwrap it so callers and Sentry see the actual failure
 * instead of the generic message.
 */
function toNetworkError(err: any, config: HttpRequestConfig): HttpError {
  const cause = err?.cause
  const code = err?.name === 'AbortError' ? 'ECONNABORTED' : err?.code ?? cause?.code
  const message =
    err?.message === 'fetch failed' && cause?.message
      ? `${err.message}: ${cause.message}`
      : err?.message || 'Network Error'
  return new HttpError(message, config, undefined, code, err)
}

async function performRequest<T>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
  const { url, authHeader } = stripUrlCredentials(buildUrl(config))
  // Coerce header values to strings (callers may pass numbers/booleans, as axios allowed).
  const headers: Record<string, string> = {}
  for (const [key, value] of Object.entries(config.headers || {})) {
    if (value !== undefined && value !== null) headers[key] = String(value)
  }

  // Match axios's default `Accept` header. Native fetch sends none, which makes
  // content-negotiating APIs (e.g. QuickBooks) default to XML — JSON callers then
  // get a body they cannot parse. The `*/*` keeps binary/text endpoints unaffected.
  if (!Object.keys(headers).some((h) => h.toLowerCase() === 'accept')) {
    headers['Accept'] = 'application/json, text/plain, */*'
  }

  if (config.auth) {
    headers['Authorization'] = `Basic ${base64(`${config.auth.username}:${config.auth.password}`)}`
  } else if (authHeader && !Object.keys(headers).some((h) => h.toLowerCase() === 'authorization')) {
    headers['Authorization'] = authHeader
  }

  const body = buildBody(config, headers)

  // Combine an explicit signal with an optional timeout.
  let signal = config.signal
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  if (config.timeout && config.timeout > 0) {
    const controller = new AbortController()
    timeoutId = setTimeout(() => controller.abort(), config.timeout)
    if (config.signal) {
      // Abort our controller if the caller's signal fires.
      if (config.signal.aborted) controller.abort()
      else config.signal.addEventListener('abort', () => controller.abort(), { once: true })
    }
    signal = controller.signal
  }

  // The timeout covers the WHOLE request: connect, headers AND the body read. It used to be cleared
  // as soon as fetch() resolved with headers, so a server that sent headers and then stalled the body
  // (a Browserless PDF render did) hung the caller with no deadline until its Lambda was killed (#25625).
  let res: Response
  let data: any
  try {
    try {
      res = await fetch(url, {
        method: (config.method || 'GET').toUpperCase(),
        headers,
        body,
        signal
      })
    } catch (err: any) {
      throw toNetworkError(err, config)
    }

    try {
      data = await parseBody(res, config.responseType || 'json')
    } catch (err: any) {
      // The body stream failed or was aborted mid-read. Same shape as a network failure: there is
      // no usable response, so `error.response` stays undefined and the default retry rule applies.
      throw toNetworkError(err, config)
    }
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }

  const response: HttpResponse<T> = {
    data,
    status: res.status,
    statusText: res.statusText,
    headers: headersToObject(res.headers),
    config
  }

  const validateStatus = config.validateStatus === undefined ? DEFAULT_VALIDATE_STATUS : config.validateStatus
  if (validateStatus && !validateStatus(res.status)) {
    throw new HttpError(`Request failed with status code ${res.status}`, config, response)
  }

  return response
}

async function requestWithRetry<T>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
  const retry = config.retry
  const retries = retry?.retries ?? 0
  const retryCondition = retry?.retryCondition ?? defaultRetryCondition
  const retryDelay = retry?.retryDelay ?? defaultRetryDelay

  let attempt = 0
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await performRequest<T>(config)
    } catch (err) {
      const error = err as HttpError
      attempt += 1
      if (attempt > retries || !isHttpError(error) || !retryCondition(error)) throw error
      retry?.onRetry?.(attempt, error, config)
      await delay(retryDelay(attempt, error))
    }
  }
}

function createInstance(defaults: HttpRequestConfig = {}): HttpInstance {
  const request = <T = any>(config: HttpRequestConfig): Promise<HttpResponse<T>> =>
    requestWithRetry<T>(mergeConfig(defaults, config))

  const instance = ((configOrUrl: HttpRequestConfig | string, config?: HttpRequestConfig) =>
    typeof configOrUrl === 'string' ? request({ ...config, url: configOrUrl }) : request(configOrUrl)) as HttpInstance

  instance.request = request
  instance.get = (url, config) => request({ ...config, method: 'GET', url })
  instance.delete = (url, config) => request({ ...config, method: 'DELETE', url })
  instance.head = (url, config) => request({ ...config, method: 'HEAD', url })
  instance.options = (url, config) => request({ ...config, method: 'OPTIONS', url })
  instance.post = (url, data, config) => request({ ...config, method: 'POST', url, data })
  instance.put = (url, data, config) => request({ ...config, method: 'PUT', url, data })
  instance.patch = (url, data, config) => request({ ...config, method: 'PATCH', url, data })
  instance.create = (config) => createInstance(mergeConfig(defaults, config || {}))
  instance.isHttpError = isHttpError
  instance.isAxiosError = isHttpError
  instance.defaults = defaults

  return instance
}

const http = createInstance()

export default http
